import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useState } from 'react'

export function Features() {
  const [activeSlide, setActiveSlide] = useState(0)

  // Text and images sourced from the WQData LIVE Web Datacenter page on the
  // NexSens website (nexsens.com/products/software/web-datacenter). Seven
  // features, in the same order as that page. Images are self-hosted under
  // /images/features/ (downloaded from the NexSens CDN).
  const features = [
    {
      title: 'Device-to-Cloud Communication',
      image: '/images/features/device-to-cloud.jpg',
      description: `Data loggers send sensor measurements to WQData LIVE, securely storing them in the cloud for easy access. Adjust settings and resolve issues from anywhere, reducing the need for on-site visits. Simply send commands, and the logger updates automatically, saving time and effort. Modify measurement intervals with a single click. Get instant email alerts when readings change. Alarms reset automatically once conditions return to normal, ensuring reliable data management.`,
    },
    {
      title: 'Customizable Dashboards',
      image: '/images/features/customizable-dashboards.jpg',
      description: `Create and personalize multiple dashboards to display data the way you need. Set them to cycle automatically or use a single, scrollable layout for a complete overview. Default dashboards can be customized to fit any project with just a few clicks. Editing is simple—click the edit icon and rearrange panels using drag-and-drop. Choose from maps, device lists, live video, project photos, graphs, alarms, and more. Build dashboards that show the most important information at a glance.`,
    },
    {
      title: 'Interactive Dashboard Displays',
      image: '/images/features/interactive-displays.jpg',
      description: `Graph one or multiple parameters by selecting them from the site list or map for a personalized data display. View data by day, week, month, or year to fit analysis needs. Zoom in on specific events for a closer look, or reset to see the full timeline. Explore project locations with ESRI Leaflet maps, using precise markers for better clarity. Click on site markers to view data and photos. Color-coded indicators and highlighted values make it easy to spot event conditions.`,
    },
    {
      title: 'Effortless Report Generation and Sharing',
      image: '/images/features/reports.jpg',
      description: `Pre-built templates create new multi-site, multi-parameter reports. Make data easier to understand by adding charts, statistics, tables, and field notes. Post to an FTP site, email, create PDF files, or download them to spreadsheets. Save reports to generate and share.`,
    },
    {
      title: 'Encourage Team Collaboration',
      image: '/images/features/team-collaboration.jpg',
      description: `Collaboration is seamless while protecting data from loss or unwanted changes. Project leaders can invite team members and set access levels, ensuring security. Members can view dashboards, create reports, set alerts, and more. Teams can upload project or site photos to the cloud. Visuals are stored in a central location for quick and easy access. Create albums of similar images to keep them organized. Include descriptions and notes to enhance data interpretation.`,
    },
    {
      title: 'Share with a larger group of stakeholders',
      image: '/images/features/stakeholders.jpg',
      description: `Make project data accessible through WQData LIVE's public portal. Create a unique project link for anyone to access the data. Include a project overview, map, photos, and data displays in a slideshow. Viewers can enable notifications of measurement changes to receive instant updates. Activate the donation option to encourage public funding and support for your project.`,
    },
    {
      title: 'Application Programming Interface',
      image: '/images/features/api.jpg',
      description: `Easily access real-time data using the WQData LIVE REST API. Send data to an external database or website for improved accessibility and control. Configure up to 10 API keys per project to manage access and security. Retrieve data within a set period, with support for approximately 5,000 data points per request.`,
    },
  ]

  const current = features[activeSlide]

  return (
    <section id="features" className="py-8 md:py-24">
      <div className="max-w-[1920px] mx-auto px-8 sm:px-6 lg:px-[140px]">
        <h2 className="common-head28 text-3xl font-bold text-[#4D4D4D] mb-7">Features</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-16 items-center features-section rounded-lg">
          {/* Feature Image */}
          <div className="flex justify-center p-4">
            <img
              src={current.image}
              alt={current.title}
              className="w-full h-auto max-w-[520px] rounded-lg"
            />
          </div>

          {/* Feature Details */}
          <div className="p-6">
            <h3 className="common-head25 text-3xl font-bold text-[#4D4D4D] mb-4 md:mb-6">
              {current.title}
            </h3>
            <p className="text-gray-700 text-[14px] md:text-xl leading-relaxed mb-8">
              {current.description}
            </p>

            {current.points && (
              <ul className="space-y-4 mb-10">
                {current.points.map((point, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="text-[#404040] font-bold text-[14px] md:text-xl">✓</span>
                    <span className="text-[#404040] text-[14px] md:text-xl">{point}</span>
                  </li>
                ))}
              </ul>
            )}


          </div>
        </div>
        {/* Slide Navigation */}
          <div className="flex justify-center items-center pt-8 border-t border-gray-200 gap-8">
            <button
              onClick={() => setActiveSlide((prev) => (prev - 1 + features.length) % features.length)}
              className="text-[#404040] hover:text-blue-700 transition"
              aria-label="Previous feature"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>
            <div className="flex gap-8">
              {features.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveSlide(idx)}
                  className={`transition w-2 h-2 rounded-full ${
                    activeSlide === idx ? 'bg-[#404040]' : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  title={features[idx].title}
                />
              ))}
            </div>
            <button
              onClick={() => setActiveSlide((prev) => (prev + 1) % features.length)}
              className="text-[#404040] hover:text-blue-700 transition"
              aria-label="Next feature"
            >
              <ChevronRight className="w-8 h-8" />
            </button>
          </div>
      </div>
    </section>
  )
}
